import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UpsellBanner } from './UpsellBanner';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  Menu,
  X,
  Home,
  MessageSquare,
  Calculator,
  BarChart3,
  Briefcase,
  Bookmark,
  User,
  HelpCircle,
  LogOut,
  Compass,
  CreditCard,
  DollarSign,
  PiggyBank,
} from 'lucide-react';

const navItems = [
  { href: '/dashboard', label: 'Inicio', icon: Home },
  { href: '/advisor', label: 'Asesor IA', icon: MessageSquare },
  { href: '/job-evaluator', label: 'Evaluador', icon: Briefcase },
  { href: '/severance', label: 'Liquidación', icon: Calculator },
  { href: '/salary-benchmark', label: 'Benchmark', icon: BarChart3 },
  { href: '/net-salary', label: 'Sueldo Neto', icon: DollarSign },
  { href: '/retiro', label: 'Retiro', icon: PiggyBank },
  { href: '/saved', label: 'Guardados', icon: Bookmark },
  { href: '/guidance', label: '¿Qué hago?', icon: HelpCircle },
];

export const Layout = ({ children }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const visibleNavItems = isAuthenticated
    ? navItems
    : navItems.filter((item) => ['/advisor', '/job-evaluator', '/severance', '/salary-benchmark', '/net-salary'].includes(item.href));

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Header */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
              <Compass className="w-7 h-7 text-[#2563EB]" />
              <span className="font-bold text-xl tracking-tight font-['Manrope']">
                Klarvi
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {visibleNavItems.slice(0, 6).map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    data-testid={`nav-${item.href.slice(1)}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all
                      ${isActive 
                        ? 'bg-[#2563EB]/10 text-[#2563EB]' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/guidance"
                    className="hidden md:flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="guidance-link"
                  >
                    <HelpCircle className="w-4 h-4" />
                    ¿Qué hago?
                  </Link>
                  <Link
                    to="/precios"
                    className="hidden md:flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="pricing-link"
                  >
                    <CreditCard className="w-4 h-4" />
                    Precios
                  </Link>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="relative h-9 w-9 rounded-full" data-testid="user-menu-trigger">
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={user?.picture} alt={user?.name} />
                          <AvatarFallback className="bg-[#2563EB] text-white text-sm">
                            {getInitials(user?.name)}
                          </AvatarFallback>
                        </Avatar>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="flex items-center gap-2 p-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user?.picture} alt={user?.name} />
                          <AvatarFallback className="bg-[#2563EB] text-white text-xs">
                            {getInitials(user?.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">{user?.name}</span>
                          <span className="text-xs text-muted-foreground">{user?.email}</span>
                          <span className="text-xs mt-0.5" data-testid="user-plan-badge">
                            Plan actual: {user?.plan === 'pro' ? (
                              <span className="text-[#2563EB] font-medium">Pro</span>
                            ) : (
                              <span className="text-muted-foreground font-medium">Gratis</span>
                            )}
                          </span>
                        </div>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/profile" className="flex items-center gap-2 cursor-pointer" data-testid="profile-menu-item">
                          <User className="w-4 h-4" />
                          Mi Perfil
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/saved" className="flex items-center gap-2 cursor-pointer" data-testid="saved-menu-item">
                          <Bookmark className="w-4 h-4" />
                          Resultados Guardados
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/precios" className="flex items-center gap-2 cursor-pointer" data-testid="pricing-menu-item">
                          <CreditCard className="w-4 h-4" />
                          Precios
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleLogout}
                        className="text-destructive focus:text-destructive cursor-pointer"
                        data-testid="logout-menu-item"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Cerrar Sesión
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/login')}
                    className="hidden md:inline-flex"
                    data-testid="layout-login-btn"
                  >
                    Iniciar Sesión
                  </Button>
                  <Button
                    onClick={() => navigate('/register')}
                    className="hidden md:inline-flex bg-[#0F172A] hover:bg-[#0F172A]/90"
                    data-testid="layout-register-btn"
                  >
                    Crear Cuenta
                  </Button>
                </>
              )}

              {/* Mobile Menu Button */}
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-trigger">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-72">
                  <div className="flex flex-col gap-4 mt-8">
                    {visibleNavItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.href;
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => setMobileOpen(false)}
                          data-testid={`mobile-nav-${item.href.slice(1)}`}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all
                            ${isActive 
                              ? 'bg-[#2563EB]/10 text-[#2563EB]' 
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                            }`}
                        >
                          <Icon className="w-5 h-5" />
                          {item.label}
                        </Link>
                      );
                    })}
                    <div className="border-t pt-4 mt-4">
                      {isAuthenticated ? (
                        <>
                          <Link
                            to="/profile"
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-muted-foreground hover:text-foreground hover:bg-muted"
                            data-testid="mobile-nav-profile"
                          >
                            <User className="w-5 h-5" />
                            Mi Perfil
                          </Link>
                          <Link
                            to="/precios"
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-muted-foreground hover:text-foreground hover:bg-muted"
                            data-testid="mobile-nav-precios"
                          >
                            <CreditCard className="w-5 h-5" />
                            Precios
                          </Link>
                          <button
                            onClick={() => {
                              setMobileOpen(false);
                              handleLogout();
                            }}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-destructive hover:bg-destructive/10 w-full"
                            data-testid="mobile-logout"
                          >
                            <LogOut className="w-5 h-5" />
                            Cerrar Sesión
                          </button>
                        </>
                      ) : (
                        <>
                          <Link
                            to="/login"
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-muted-foreground hover:text-foreground hover:bg-muted"
                            data-testid="mobile-nav-login"
                          >
                            <User className="w-5 h-5" />
                            Iniciar Sesión
                          </Link>
                          <Link
                            to="/register"
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-[#2563EB] hover:bg-[#2563EB]/10"
                            data-testid="mobile-nav-register"
                          >
                            <User className="w-5 h-5" />
                            Crear Cuenta
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">
        {children}
      </main>

      {/* Legal Footer */}
      <footer className="border-t mt-auto">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex flex-col items-center gap-2">
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            La información proporcionada por Klarvi es solo para fines informativos y educativos, 
            y no constituye asesoramiento legal, representación legal, asesoramiento fiscal ni asesoramiento financiero. 
            Los resultados pueden variar según las circunstancias individuales, contratos y regulaciones locales.
          </p>
          <div className="flex items-center gap-3">
            <a href="/privacidad" className="text-xs text-[#2563EB] hover:underline" data-testid="footer-privacy-link">
              Aviso de privacidad
            </a>
            <span className="text-xs text-muted-foreground">·</span>
            <a href="/terminos" className="text-xs text-[#2563EB] hover:underline" data-testid="footer-terms-link">
              Términos de servicio
            </a>
          </div>
        </div>
      </footer>

      {/* Upsell Banner - only for authenticated Free users */}
      {isAuthenticated && user?.plan !== 'pro' && <UpsellBanner />}
    </div>
  );
};

export default Layout;
